<script setup>
import { computed } from 'vue'

const props = defineProps({
  url: {
    type: String,
    required: true
  },
  text: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    default: null
  },
  variant: {
    type: String,
    default: 'default'
  },
  badge: {
    type: String,
    default: null
  }
})

const baseUrl = window.location.origin

const isInternalLink = computed(() => {
  if (!props.url) return false
  // Relative path starting with /
  if (props.url.startsWith('/') && !props.url.startsWith('//')) return true
  // Full URL matching base route
  if (props.url.startsWith(baseUrl)) return true
  return false
})

const internalPath = computed(() => {
  if (!props.url) return '/'
  // Already a relative path
  if (props.url.startsWith('/') && !props.url.startsWith('//')) return props.url
  // Extract path from full URL matching base route
  if (props.url.startsWith(baseUrl)) {
    return props.url.slice(baseUrl.length) || '/'
  }
  return props.url
})

const buttonClasses = computed(() => {
  const classes = ['action-button']
  if (props.variant === 'secondary') classes.push('action-button--secondary')
  return classes
})

const iconValue = computed(() => {
  const iconMap = {
    'bolt': 'bolt',
    'search': 'magnifying-glass',
    'eye': 'eye',
    'external-link': 'square-up-right',
    'arrow-right': 'arrow-right'
  }
  return ['fas', iconMap[props.icon] || 'arrow-right']
})
</script>

<template>
  <router-link
    v-if="isInternalLink"
    :to="internalPath"
    :class="buttonClasses"
  >
    <font-awesome-icon v-if="icon" :icon="iconValue" class="action-button__icon" />
    <span class="action-button__text">{{ text }}</span>
    <span v-if="badge" class="action-button__badge">{{ badge }}</span>
  </router-link>
  <a
    v-else
    :href="url"
    :class="buttonClasses"
    target="_blank"
    rel="noopener noreferrer"
  >
    <font-awesome-icon v-if="icon" :icon="iconValue" class="action-button__icon" />
    <span class="action-button__text">{{ text }}</span>
    <span v-if="badge" class="action-button__badge">{{ badge }}</span>
  </a>
</template>

<style scoped>
.action-button {
  height: 2.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
}

.action-button:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
}

.action-button__icon {
  font-size: 0.625rem;
  color: #f59e0b;
}

.action-button__badge {
  background: #f59e0b;
  color: #1a1f28;
  padding: 0.125rem 0.375rem;
  border-radius: 0.75rem;
  font-size: 0.625rem;
  font-weight: 600;
}

/* Secondary variant - purple accent */
.action-button--secondary {
  border-color: rgba(115, 87, 210, 0.2);
}

.action-button--secondary:hover {
  background: rgba(115, 87, 210, 0.1);
  border-color: rgba(115, 87, 210, 0.3);
}

.action-button--secondary .action-button__icon {
  color: #7357D2;
}

.action-button--secondary .action-button__badge {
  background: #7357D2;
  color: #fff;
}
</style>
